import { BetPickDetail } from '@mobx/stores/kol/kol-store';
import { DeepReadonly } from '@src/utils/type-utils';
import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import FeatureStar from '@public/images/kol/Star.svg';
import Selection from './selection';
import BetPickButton from './bet-pick-button';
import BetPickCaption from './bet-pick-caption';
import { useTranslation } from 'next-i18next';
import { withBasePath, withFullPath } from '@src/common/path-convert';
import bettingStrategyStore from '@mobx/stores/bettingStrategy-store';
import { observer } from 'mobx-react-lite';
import { reaction } from 'mobx';
import { nonPageTracker } from '@src/adobe-launch/data-layer-provider';
import { TrackingEvent } from '@src/adobe-launch/types';
import { convertArticleInfo } from '@src/hooks/tracker/use-article-tracker';
import { ContentBlockFragment } from '@graphqlTypes/sitecore';

type Props = {
    betpickDetail: DeepReadonly<BetPickDetail>;
    type: 'leader' | 'race';
    index?: number;
    isKolProfile?: boolean;
    videoEnable?: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    jsBridge?: any;
    contentBlock?: ContentBlockFragment | null;
};

interface TableProps {
    name: string;
    text: string;
    iconSrc: string;
}

/** 手动输入 */
const BetPickEntry = ({
    index,
    betpickDetail,
    type,
    videoEnable,
    jsBridge,
    isKolProfile = false,
    contentBlock,
}: Props) => {
    const { t } = useTranslation('kol');
    const { betpick, race, raceDay } = betpickDetail;
    const showBanker = useMemo(() => Boolean(betpick.bankerRow?.value), [betpick.bankerRow?.value]);
    const isStrike = betpick.buttonStatus?.value === 'Strike';

    const {
        bankerRow,
        bankerRowSCR,
        selRow,
        selRowSCR,
        selRow2nd,
        selRow2ndSCR,
        selRow3rd,
        selRow3rdSCR,
        selRow4th,
        selRow4thSCR,
        bettingStrategyType,
        bankerRowIcon,
        bankerRowTips,
        selRowIcon,
        selRowTips,
        selRow2ndIcon,
        selRow2ndTips,
        selRow3rdIcon,
        selRow3rdTips,
        selRow4thIcon,
        selRow4thTips,
        singleTipsIcon,
        singleTips,
    } = betpick;

    /** 显示底部的betting stategy */
    const showBettingStrategy = () => {
        if (bettingStrategyType?.value === 'One total tip') {
            const isShow = singleTips?.rendered?.trim() !== '';
            bettingStrategyStore.setBettingBTNList(isShow);
            return isShow;
        } else if (bettingStrategyType?.value === 'Multiple tips') {
            const isShow = whiteBData.some((item) => item.text.trim() !== '');
            bettingStrategyStore.setBettingBTNList(isShow);
            return isShow;
        } else {
            bettingStrategyStore.setResetList();
            return false;
        }
    };
    /** 只有一个tip显示 */
    const isOnlyOneTip = bettingStrategyType?.value === 'One total tip';

    // Whiteboard open
    const [isOpen, setIsOpen] = useState(bettingStrategyStore.bettingDefault);

    // whiteboard data
    const [whiteBData, setWhiteBData] = useState<TableProps[]>([]);

    // console.log('==whiteBData==', whiteBData);

    const toggleOpen = () => {
        nonPageTracker(
            {
                eventID: isOpen
                    ? TrackingEvent.CollapseBettingStrategy
                    : TrackingEvent.ExpandBettingStrategy,
                raceID: `${raceDay?.name}/${race?.name}`,
                kolName: betpick.kol?.targetItem?.name,
            },
            { article: convertArticleInfo(contentBlock) }
        );

        setIsOpen((prev) => !prev);
    };

    /** table 数据封装 */
    const tableDataFun = useCallback(() => {
        const createTableProps = (
            rowValue: string | undefined,
            rowSCRBool: boolean | undefined,
            tipsValue: string | undefined,
            iconSrc: string | undefined
        ): TableProps | null => {
            if (rowValue) {
                const bankerName = `${rowValue} ${rowSCRBool ? `(${t('betpick.scratched')})` : ''}`;
                return {
                    name: bankerName,
                    text: tipsValue || '',
                    iconSrc: iconSrc || '',
                };
            }
            return null;
        };

        const rows = [
            {
                value: bankerRow?.value,
                scrBool: bankerRowSCR?.boolValue,
                tipsValue: bankerRowTips?.rendered,
                iconSrc: bankerRowIcon?.src,
            },
            {
                value: selRow?.value,
                scrBool: selRowSCR?.boolValue,
                tipsValue: selRowTips?.rendered,
                iconSrc: selRowIcon?.src,
            },
            {
                value: selRow2nd?.value,
                scrBool: selRow2ndSCR?.boolValue,
                tipsValue: selRow2ndTips?.rendered,
                iconSrc: selRow2ndIcon?.src,
            },
            {
                value: selRow3rd?.value,
                scrBool: selRow3rdSCR?.boolValue,
                tipsValue: selRow3rdTips?.rendered,
                iconSrc: selRow3rdIcon?.src,
            },
            {
                value: selRow4th?.value,
                scrBool: selRow4thSCR?.boolValue,
                tipsValue: selRow4thTips?.rendered,
                iconSrc: selRow4thIcon?.src,
            },
        ];

        const list = rows.flatMap(({ value, scrBool, tipsValue, iconSrc }) => {
            const result = createTableProps(value, scrBool, tipsValue, iconSrc);
            return result ? [result] : [];
        });

        setWhiteBData(list);
    }, [
        bankerRow?.value,
        bankerRowIcon?.src,
        bankerRowSCR?.boolValue,
        bankerRowTips?.rendered,
        selRow?.value,
        selRow2nd?.value,
        selRow2ndIcon?.src,
        selRow2ndSCR?.boolValue,
        selRow2ndTips?.rendered,
        selRow3rd?.value,
        selRow3rdIcon?.src,
        selRow3rdSCR?.boolValue,
        selRow3rdTips?.rendered,
        selRow4th?.value,
        selRow4thIcon?.src,
        selRow4thSCR?.boolValue,
        selRow4thTips?.rendered,
        selRowIcon?.src,
        selRowSCR?.boolValue,
        selRowTips?.rendered,
        t,
    ]);

    useEffect(() => {
        tableDataFun();
    }, [tableDataFun]);

    useEffect(() => {
        const disposer = reaction(
            () => bettingStrategyStore.bettingDefault,
            (newValue) => {
                setIsOpen(newValue);
            }
        );

        return () => {
            disposer();
        };
    }, []);

    return (
        <div
            className={classNames(
                'relative overflow-clip flex flex-col items-stretch bg-white min-h-[158px] px-3 pb-6 rounded-[8px]',
                { 'border-[#FF9900]': betpick.isFeatured?.boolValue },
                { 'border-[4px]': betpick.isFeatured?.boolValue },
                { 'border-white': !betpick.isFeatured?.boolValue },
                { 'pt-[30px]': isStrike }
            )}
            style={{ boxShadow: '0 2px 8px 0 rgba(0, 0, 0, 0.15)' }}
        >
            <div>
                <BetPickCaption
                    index={index}
                    betpickDetail={betpickDetail}
                    type={type}
                    videoEnable={videoEnable}
                    jsBridge={jsBridge}
                    contentBlock={contentBlock}
                ></BetPickCaption>
            </div>
            <div className='bg-[#E0E0E0] h-px self-stretch mt-2 md:mt-3'></div>
            <div className='grow pt-1 md:pt-2 leading-[16px]'>
                {!isKolProfile && (
                    <div className='font-bold min-h-[16px] text-[15px]'>
                        {betpick.betPickType?.targetItem?.betTypeName?.value}
                    </div>
                )}
                <div className='flex  items-start align-middle mt-2'>
                    <div className='w-full'>
                        <div className=''>
                            {showBanker && (
                                <div className='flex items-start'>
                                    <div className='bg-[#173E96] text-white text-[8px] text-center px-1  min-w-[40px] rounded-sm mr-1'>
                                        {t('betpick.banker')}
                                    </div>
                                    <div
                                        className={classNames({
                                            'w-[calc(100%-40px)]': showBanker,
                                            'w-full': !showBanker,
                                        })}
                                    >
                                        <Selection
                                            value={betpick.bankerRow?.value}
                                            scratched={betpick.bankerRowSCR?.boolValue}
                                        ></Selection>
                                    </div>
                                </div>
                            )}

                            <div className='flex items-start'>
                                {showBanker && (
                                    <div className='bg-[#173E96] text-white text-[8px] text-center px-1  min-w-[40px] rounded-sm mr-1'>
                                        {t('betpick.selection')}
                                    </div>
                                )}
                                <div
                                    className={classNames({
                                        'w-[calc(100%-40px)]': showBanker,
                                        'w-full': !showBanker,
                                    })}
                                >
                                    <Selection
                                        value={betpick.selRow?.value}
                                        scratched={betpick.selRowSCR?.boolValue}
                                    ></Selection>
                                    <Selection
                                        className={classNames({ 'mt-2': !showBanker })}
                                        value={betpick.selRow2nd?.value}
                                        scratched={betpick.selRow2ndSCR?.boolValue}
                                    ></Selection>
                                    <Selection
                                        className={classNames({ 'mt-2': !showBanker })}
                                        value={betpick.selRow3rd?.value}
                                        scratched={betpick.selRow3rdSCR?.boolValue}
                                    ></Selection>
                                    <Selection
                                        className={classNames({ 'mt-2': !showBanker })}
                                        value={betpick.selRow4th?.value}
                                        scratched={betpick.selRow4thSCR?.boolValue}
                                    ></Selection>
                                </div>
                            </div>
                        </div>
                        <div className='min-w-full'>
                            <BetPickButton betpickDetail={betpickDetail}></BetPickButton>
                        </div>
                        {/* hide betting strategy */}
                        {showBettingStrategy() && (
                            <div className='flex justify-center items-center flex-col mt-3'>
                                <div
                                    className='font-medium text-sm text-[#003C84] flex items-center'
                                    onClick={toggleOpen}
                                >
                                    <img
                                        className={`w-5 h-5 mr-1`}
                                        src={withBasePath('/images/kol/i-win2.svg')}
                                        alt=''
                                        loading='lazy'
                                    />
                                    {isOpen ? t('HideBetting') : t('ShowBetting')}
                                    <img
                                        className={`w-5 h-5 ml-1 transition-transform duration-300 ${
                                            isOpen ? 'rotate-180' : ''
                                        }`}
                                        src={withBasePath('/images/kol/i-down.svg')}
                                        alt=''
                                        loading='lazy'
                                    />
                                </div>

                                {/* Whiteboard */}
                                <div className='w-full'>
                                    <div
                                        className={`transition-max-height duration-300 ease-in-out overflow-hidden ${
                                            isOpen ? 'max-h-[90000000px]' : 'max-h-0'
                                        }`}
                                    >
                                        {/* content */}
                                        {isOnlyOneTip ? (
                                            <>
                                                <div className='pt-2' key={index}>
                                                    {singleTips?.rendered && (
                                                        <div className='bg-[#EAF5FF] rounded py-2 px-2 flex items-center relative'>
                                                            {singleTipsIcon?.src && (
                                                                <img
                                                                    className={`w-6 h-6 mr-2`}
                                                                    src={withFullPath(
                                                                        singleTipsIcon?.src
                                                                    )}
                                                                    alt=''
                                                                    loading='lazy'
                                                                />
                                                            )}
                                                            <div
                                                                className='text-sm text-black font-normal break-normal'
                                                                dangerouslySetInnerHTML={{
                                                                    __html:
                                                                        singleTips?.rendered || '',
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {whiteBData.map((i, index) => (
                                                    <div className='pt-2' key={index}>
                                                        {i.text && (
                                                            <div className='text-sm font-bold text-black'>
                                                                {i.name}
                                                            </div>
                                                        )}
                                                        {i.text && (
                                                            <div className='bg-[#EAF5FF] rounded py-2 px-2 flex items-center relative mt-2'>
                                                                <div className='tooltipStyleA'></div>
                                                                {i.iconSrc && (
                                                                    <img
                                                                        className={`w-6 h-6 mr-2`}
                                                                        src={withFullPath(
                                                                            i.iconSrc
                                                                        )}
                                                                        alt=''
                                                                        loading='lazy'
                                                                    />
                                                                )}
                                                                <div
                                                                    className='text-sm text-black font-normal break-normal'
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: i.text || '',
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {betpick.isFeatured?.boolValue && (
                <FeatureStar className='absolute -bottom-1 -right-1'></FeatureStar>
            )}
        </div>
    );
};

export default observer(BetPickEntry);
